<template>
  <div class="chat-session-header">
    <!-- Left: back, avatar, titles -->
    <div class="header-left">
      <button class="back-btn" @click="goBackToSessions" title="Back to Sessions">
        <i class="las la-arrow-left"></i>
      </button>
      <div class="candidate-avatar">
        {{ getInitials(selectedCandidate.name) }}
      </div>
      <div class="header-titles">
        <h2 class="session-title" :title="chatSession.chat_header || 'Untitled Session'">
          {{ chatSession.chat_header || 'Untitled Session' }}
        </h2>
        <p class="candidate-line">
          <span>{{ selectedCandidate.name }}</span>
          <span v-if="selectedCandidate.company"> · {{ selectedCandidate.company }}</span>
        </p>
      </div>
    </div>

    <!-- Right: date pill + files control -->
    <div class="header-right">
      <span v-if="chatSession.created_at" class="date-pill">
        <i class="las la-calendar"></i>
        {{ formatDate(chatSession.created_at) }}
      </span>

      <div class="files-control" ref="filesControl">
        <button
          class="files-btn"
          :class="{ active: showFilesPopover }"
          @click="toggleFilesPopover"
        >
          <i class="las la-paperclip"></i>
          <span>Files ({{ sessionFiles.length }})</span>
          <i class="caret las la-angle-down" :class="{ open: showFilesPopover }"></i>
        </button>

        <!-- Files popover -->
        <transition name="popover-fade">
          <div v-if="showFilesPopover" class="files-popover">
            <div class="popover-header">
              <span class="popover-title">Session Files</span>
              <button class="add-btn" @click="openFileSelector">
                <i class="las la-plus"></i>
                Add
              </button>
            </div>

            <div v-if="sessionFiles.length === 0" class="popover-empty">
              No files yet — add files to chat about them.
            </div>
            <div v-else class="popover-list">
              <div
                v-for="file in sessionFiles"
                :key="file.id"
                class="popover-file"
                :class="{ selected: selectedFile && selectedFile.id === file.id }"
                @click="selectFile(file)"
              >
                <i class="file-icon las la-file-pdf"></i>
                <span class="file-name">{{ file.file_name || file.name }}</span>
                <button
                  class="remove-btn"
                  @click.stop="removeFile(file.id)"
                  :disabled="removingFileId === file.id"
                  :title="removingFileId === file.id ? 'Removing...' : 'Remove from session'"
                >
                  <BaseSpinner v-if="removingFileId === file.id" size="small" />
                  <i v-else class="las la-times"></i>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- File Selector Modal -->
    <div v-if="showFileSelector" class="file-selector-modal">
      <div class="modal-overlay" @click="closeFileSelector"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h4>Select Files to Add</h4>
          <div class="modal-header-actions">
            <button class="close-btn" @click="closeFileSelector">
              <i class="las la-times"></i>
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div v-if="loadingFiles" class="loading-files">
            <BaseSpinner size="small" />
            <p>Loading available files...</p>
          </div>
          <div v-else-if="availableFiles.length === 0" class="no-available-files">
            <p>No files available to add.</p>
            <button class="upload-new-btn" @click="goToUpload">
              <i class="las la-upload"></i>
              Upload New File
            </button>
          </div>
          <div v-else class="files-table-container">
            <div class="files-table-header">
              <label class="select-all-checkbox">
                <input
                  type="checkbox"
                  :checked="allFilesSelected"
                  @change="toggleSelectAll"
                />
                <span>Select All</span>
              </label>
              <button
                class="add-selected-btn"
                @click="addSelectedFiles"
                :disabled="selectedFiles.length === 0 || addingFiles"
              >
                <BaseSpinner v-if="addingFiles" size="small" />
                <i v-else class="las la-plus"></i>
                {{ addingFiles ? 'Adding...' : `Add Selected (${selectedFiles.length})` }}
              </button>
            </div>
            <div class="files-table">
              <div
                v-for="file in availableFiles"
                :key="file.id"
                class="files-table-row"
                :class="{ 'selected': isFileSelected(file.id) }"
                @click="toggleFileSelection(file)"
              >
                <label class="file-checkbox" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isFileSelected(file.id)"
                    @change="toggleFileSelection(file)"
                  />
                </label>
                <div class="file-icon">
                  <i class="las la-file-pdf"></i>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.file_name || file.name }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";

export default {
  name: 'ChatSessionHeader',
  components: {
    BaseSpinner
  },
  props: {
    selectedCandidate: {
      type: Object,
      required: true
    },
    chatSession: {
      type: Object,
      required: true
    },
    sessionFiles: {
      type: Array,
      default: () => []
    },
    selectedFile: {
      type: Object,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showFilesPopover: false,
      showFileSelector: false,
      availableFiles: [],
      selectedFiles: [],
      loadingFiles: false,
      removingFileId: null,
      addingFiles: false
    }
  },
  computed: {
    allFilesSelected() {
      return this.availableFiles.length > 0 &&
             this.selectedFiles.length === this.availableFiles.length;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    toggleFilesPopover() {
      this.showFilesPopover = !this.showFilesPopover;
    },

    handleOutsideClick(event) {
      if (!this.showFilesPopover) return;
      const control = this.$refs.filesControl;
      if (control && !control.contains(event.target)) {
        this.showFilesPopover = false;
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.showFilesPopover = false;
        if (this.showFileSelector) this.closeFileSelector();
      }
    },

    async openFileSelector() {
      this.showFilesPopover = false;
      this.showFileSelector = true;
      await this.loadAvailableFiles();
    },

    closeFileSelector() {
      this.showFileSelector = false;
      this.availableFiles = [];
      this.selectedFiles = [];
    },

    async loadAvailableFiles() {
      this.loadingFiles = true;
      try {
        const files = await ChatbotService.getUploads(this.selectedCandidate.id, { type: 'bank_statement,multi_account_statement,emirates_id,trade_license,vat_statement', page: 1, page_size: 256 });
        let allFiles = Array.isArray(files) ? files : (files.files || files.data || []);

        this.availableFiles = allFiles.filter(file =>
          !this.sessionFiles.some(sessionFile => sessionFile.id === file.id)
        );
      } catch (err) {
        console.error('Error loading available files:', err);
        this.availableFiles = [];
      } finally {
        this.loadingFiles = false;
      }
    },

    isFileSelected(fileId) {
      return this.selectedFiles.some(f => f.id === fileId);
    },

    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.id === file.id);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
      } else {
        this.selectedFiles.push(file);
      }
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedFiles = [...this.availableFiles];
      } else {
        this.selectedFiles = [];
      }
    },

    async addSelectedFiles() {
      if (this.selectedFiles.length === 0) return;

      this.addingFiles = true;
      try {
        this.$emit('files-added', this.selectedFiles);
        this.closeFileSelector();
      } catch (error) {
        console.error('Error adding selected files:', error);
        this.$bvToast.toast('Error adding files: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.addingFiles = false;
      }
    },

    async removeFile(fileId) {
      this.removingFileId = fileId;
      try {
        this.$emit('file-removed', fileId);
      } finally {
        setTimeout(() => {
          this.removingFileId = null;
        }, 500);
      }
    },

    selectFile(file) {
      this.$emit('file-selected', file);
    },

    goToUpload() {
      this.$router.push({
        name: 'chatbot-upload',
        params: {
          candidateId: this.selectedCandidate.id
        }
      });
    },

    goBackToSessions() {
      this.$router.push({
        name: 'chatbot-candidate',
        params: { candidateId: this.selectedCandidate.id }
      });
    },

    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return '';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.chat-session-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #eef2f6;
  background: #ffffff;
  flex-shrink: 0;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.back-btn {
  background: #f1f5f9;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #e2e8f0;
    border-color: #cbd5e0;
    color: #2d3748;
  }

  i {
    font-size: 1rem;
    color: inherit;
  }
}

.candidate-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
  color: $color-secondary;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
}

.header-titles {
  min-width: 0;
  display: flex;
  flex-direction: column;

  .session-title {
    margin: 0;
    font-family: $brand-font-medium;
    font-size: 1.05rem;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 46ch;
  }

  .candidate-line {
    margin: 0;
    font-family: $brand-font-regular;
    font-size: 0.8rem;
    color: #718096;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.date-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: $brand-font-regular;
  font-size: 0.82rem;
  color: #718096;
  background: #f7fafc;
  border: 1px solid #eef2f6;
  border-radius: 20px;
  padding: 0.35rem 0.75rem;
  white-space: nowrap;

  i {
    font-size: 0.9rem;
  }
}

.files-control {
  position: relative;
}

.files-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #ffffff;
  color: #2d3748;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.45rem 0.85rem;
  font-family: $brand-font-medium;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  i {
    font-size: 0.95rem;
    color: darken($color-primary, 22%);
  }

  .caret {
    font-size: 0.7rem;
    color: #a0aec0;
    transition: transform 0.2s ease;

    &.open {
      transform: rotate(180deg);
    }
  }

  &:hover {
    border-color: $color-primary;
    background: rgba($color-primary, 0.06);
  }

  &.active {
    border-color: $color-primary;
    background: rgba($color-primary, 0.1);
  }
}

.files-popover {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 320px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  overflow: hidden;
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.popover-fade-enter,
.popover-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #eef2f6;

  .popover-title {
    font-family: $brand-font-medium;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2d3748;
  }

  .add-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: $color-primary;
    color: $color-secondary;
    border: none;
    border-radius: 8px;
    padding: 0.35rem 0.7rem;
    font-family: $brand-font-medium;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    i {
      font-size: 0.85rem;
    }

    &:hover {
      background: darken($color-primary, 10%);
    }
  }
}

.popover-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: #a0aec0;
}

.popover-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
}

.popover-file {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  .file-icon {
    color: #e53e3e;
    font-size: 1.15rem;
    flex-shrink: 0;

    &.las {
      color: #e53e3e;
    }
  }

  .file-name {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    color: #2d3748;
    font-weight: 500;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .remove-btn {
    background: none;
    color: #e53e3e;
    border: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    opacity: 0;

    &:hover:not(:disabled) {
      background: #fed7d7;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    i {
      font-size: 0.85rem;
    }
  }

  &:hover {
    background: #f7fafc;

    .remove-btn {
      opacity: 1;
    }
  }

  &.selected {
    background: rgba($color-primary, 0.12);
    border-color: $color-primary;

    .remove-btn {
      opacity: 1;
    }
  }
}

// File Selector Modal
.file-selector-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    margin: 0;
    font-size: 1.2rem;
    color: #2d3748;
    font-weight: 600;
  }

  .modal-header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #718096;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f7fafc;
      color: #4a5568;
    }
  }
}

.modal-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
}

.loading-files {
  text-align: center;
  padding: 2rem;
  color: #718096;

  .base-spinner {
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
  }
}

.no-available-files {
  text-align: center;
  padding: 2rem;
  color: #718096;

  p {
    margin: 0 0 1rem 0;
    font-size: 1rem;
  }

  .upload-new-btn {
    background: $color-primary;
    color: $color-secondary;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      background: darken($color-primary, 10%);
    }

    i {
      font-size: 1rem;
    }
  }
}

.files-table-container {
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.files-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0 0.75rem;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 0.5rem;
  flex-shrink: 0;

  .select-all-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    margin: 0;

    input[type="checkbox"] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      box-sizing: border-box;
      width: 18px;
      height: 18px;
      min-width: 18px;
      margin: 0;
      padding: 0;
      flex-shrink: 0;
      border: 2px solid #cbd5e0;
      border-radius: 5px;
      background: #ffffff;
      cursor: pointer;
      position: relative;
      vertical-align: middle;

      // Neutralise the global common.scss checkbox pseudo-element
      &::after {
        content: none;
        display: none;
        width: auto;
        height: auto;
        margin: 0;
        background: none;
        border: none;
      }

      &:hover {
        border-color: $color-primary;
      }

      &:checked {
        background: $color-primary;
        border-color: $color-primary;

        &::after {
          content: '✓' !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          position: absolute;
          inset: 0;
          width: auto !important;
          height: auto !important;
          margin: 0 !important;
          background: none !important;
          border: none !important;
          border-radius: 0;
          color: #ffffff !important;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          text-align: center;
          transform: none;
        }
      }
    }
  }

  .add-selected-btn {
    background: $color-primary;
    color: $color-secondary;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: $brand-font-medium;

    &:hover:not(:disabled) {
      background: darken($color-primary, 10%);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    i {
      font-size: 1rem;
    }
  }
}

.files-table {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
}

.files-table-row {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #edf2f7;
    border-color: $color-primary;
  }

  &.selected {
    background: rgba($color-primary, 0.1);
    border-color: $color-primary;
  }

  .file-checkbox {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    margin: 0;

    input[type="checkbox"] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      box-sizing: border-box;
      width: 18px;
      height: 18px;
      min-width: 18px;
      margin: 0;
      padding: 0;
      flex-shrink: 0;
      border: 2px solid #cbd5e0;
      border-radius: 5px;
      background: #ffffff;
      cursor: pointer;
      position: relative;
      vertical-align: middle;

      // Neutralise the global common.scss checkbox pseudo-element
      &::after {
        content: none;
        display: none;
        width: auto;
        height: auto;
        margin: 0;
        background: none;
        border: none;
      }

      &:hover {
        border-color: $color-primary;
      }

      &:checked {
        background: $color-primary;
        border-color: $color-primary;

        &::after {
          content: '✓' !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          position: absolute;
          inset: 0;
          width: auto !important;
          height: auto !important;
          margin: 0 !important;
          background: none !important;
          border: none !important;
          border-radius: 0;
          color: #ffffff !important;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          text-align: center;
          transform: none;
        }
      }
    }
  }

  .file-icon {
    color: #e53e3e;
    font-size: 1.25rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    i {
      color: #e53e3e;
    }
  }

  .file-info {
    flex: 1;
    min-width: 0;

    .file-name {
      font-size: 0.9rem;
      color: #2d3748;
      font-weight: 500;
      line-height: 1.3;
      word-break: break-word;
    }
  }
}

// Responsive
@media (max-width: 768px) {
  .chat-session-header {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .header-titles .session-title {
    max-width: 60vw;
  }

  .date-pill {
    display: none;
  }

  .files-popover {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    border-radius: 16px 16px 0 0;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}
</style>
